/**
 * @taskId T649
 * @see functional/Cycles/cycle-5/tasks.md HU-25-FE
 */
import { Card, CardContent, Skeleton } from '@flexibilitypay/ui';
import type { BreakdownItem } from './api';

interface BreakdownChartProps {
  data: BreakdownItem[] | undefined;
  isLoading: boolean;
  periodLabel?: string;
}

const BAR_HEIGHT = 32;
const BAR_GAP = 8;
const CHART_PADDING_LEFT = 140;
const CHART_PADDING_RIGHT = 60;
const CHART_PADDING_Y = 16;
const MIN_WIDTH = 60;

function formatArs(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value}`;
}

export function BreakdownChart({ data, isLoading, periodLabel }: BreakdownChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="mb-4 h-5 w-48" />
          <Skeleton className="h-[200px] rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-[200px] items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">
            Sin datos de breakdown para el período seleccionado
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.total), MIN_WIDTH);
  const chartWidth = 560;
  const innerWidth = chartWidth - CHART_PADDING_LEFT - CHART_PADDING_RIGHT;
  const chartHeight =
    data.length * (BAR_HEIGHT + BAR_GAP) - BAR_GAP + CHART_PADDING_Y * 2;

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="mb-4 text-sm font-semibold text-foreground">
          Volumen por merchant{periodLabel ? ` — ${periodLabel}` : ''}
        </h3>
        <div className="overflow-x-auto">
          <svg
            width="100%"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            role="img"
            aria-label="Gráfico de volumen por merchant"
          >
            {data.map((item, i) => {
              const barWidth = Math.max(
                (item.total / maxValue) * innerWidth,
                4,
              );
              const y = CHART_PADDING_Y + i * (BAR_HEIGHT + BAR_GAP);
              const barY = y + (BAR_HEIGHT - 20) / 2;

              return (
                <g key={item.merchantId}>
                  {/* merchant label */}
                  <text
                    x={CHART_PADDING_LEFT - 8}
                    y={y + BAR_HEIGHT / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={12}
                    className="fill-muted-foreground font-sans"
                    fill="currentColor"
                  >
                    {item.merchantName.length > 16
                      ? `${item.merchantName.slice(0, 15)}…`
                      : item.merchantName}
                  </text>

                  {/* bar background */}
                  <rect
                    x={CHART_PADDING_LEFT}
                    y={barY}
                    width={innerWidth}
                    height={20}
                    rx={4}
                    className="fill-muted"
                    fill="currentColor"
                    opacity={0.15}
                  />

                  {/* bar fill */}
                  <rect
                    x={CHART_PADDING_LEFT}
                    y={barY}
                    width={barWidth}
                    height={20}
                    rx={4}
                    fill="#6366f1"
                    opacity={0.85}
                  />

                  {/* value label */}
                  <text
                    x={CHART_PADDING_LEFT + barWidth + 6}
                    y={barY + 10}
                    dominantBaseline="middle"
                    fontSize={11}
                    className="fill-foreground font-sans"
                    fill="currentColor"
                  >
                    {formatArs(item.total)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </CardContent>
    </Card>
  );
}
